import { useEffect, useRef, useState } from 'react'

/**
 * Penn Review trigger modal. Date range pickers (both required, no
 * defaults — see [[feedback_create_block_all_day_default]] mindset) +
 * optional emphasis note + write-to-vault toggle. Submit calls the parent's
 * `onSubmit` with the validated payload; the parent flips the chat into
 * penn_review mode and lets the auto-greet effect fire the SSE stream.
 *
 * Visual treatment mirrors the concierge page: dark bg, hairline borders,
 * no rounded corners, Atlas Grotesk via inheritance.
 */
export interface PennReviewModalSubmit {
  dateFrom: string
  dateTo: string
  contextNote: string
  writeToVault: boolean
}

interface Props {
  open: boolean
  onClose: () => void
  onSubmit: (payload: PennReviewModalSubmit) => void
}

export function PennReviewModal({ open, onClose, onSubmit }: Props) {
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [contextNote, setContextNote] = useState('')
  const [writeToVault, setWriteToVault] = useState(true)
  const fromRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (open) {
      // Reset to empty on each open so a previous run's dates don't linger.
      setDateFrom('')
      setDateTo('')
      setContextNote('')
      setWriteToVault(true)
      // Autofocus the first date picker so keyboard users can pick a date
      // immediately. setTimeout breaks past React's render commit.
      setTimeout(() => fromRef.current?.focus(), 0)
    }
  }, [open])

  // ESC closes — matches the chat's mode-exit affordance.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  const valid = !!(dateFrom && dateTo && dateFrom <= dateTo)
  const rangeError =
    dateFrom && dateTo && dateFrom > dateTo
      ? 'date_from must be on or before date_to'
      : null

  const handleSubmit = () => {
    if (!valid) return
    onSubmit({ dateFrom, dateTo, contextNote: contextNote.trim(), writeToVault })
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="bg-background text-foreground border border-border w-[460px] max-w-[92vw] p-6 shadow-lg">
        <div className="mb-4">
          <div className="text-[15px] mb-1">Penn Review</div>
          <div className="text-[12px] text-muted-foreground leading-relaxed">
            Reviews every PENN workstream with completed blocks in the date
            range. One agent per workstream runs in parallel; a consolidator
            stitches the card and saves it to penn/Penn Review/.
          </div>
        </div>

        <div className="space-y-3">
          <label className="block">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">
              Date from
            </div>
            <input
              ref={fromRef}
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-full px-3 py-2 text-[13px] bg-background border border-border text-foreground focus:outline-none focus:border-foreground"
            />
          </label>

          <label className="block">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">
              Date to
            </div>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-full px-3 py-2 text-[13px] bg-background border border-border text-foreground focus:outline-none focus:border-foreground"
            />
          </label>

          {rangeError && (
            <div className="text-[12px] text-red-400">{rangeError}</div>
          )}

          <label className="block">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">
              Emphasis (optional)
            </div>
            <input
              type="text"
              value={contextNote}
              onChange={(e) => setContextNote(e.target.value)}
              placeholder="e.g. focus on the Alton thread"
              className="w-full px-3 py-2 text-[13px] bg-background border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-foreground"
            />
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={writeToVault}
              onChange={(e) => setWriteToVault(e.target.checked)}
              className="accent-foreground"
            />
            <span className="text-[12px] text-foreground">
              Auto-write to <code className="text-muted-foreground">penn/Penn Review/</code>
            </span>
          </label>
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="text-[12px] px-3 py-1.5 border border-border text-muted-foreground hover:text-foreground transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!valid}
            className={`text-[12px] px-3 py-1.5 border transition-colors ${
              valid
                ? 'border-foreground text-foreground hover:bg-[rgba(255,255,255,0.05)] cursor-pointer'
                : 'border-border text-muted-foreground cursor-not-allowed opacity-60'
            }`}
          >
            Run Penn Review
          </button>
        </div>
      </div>
    </div>
  )
}
